import React, { useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const TEAL      = '#0d9488'
const TEAL_DARK = '#0f766e'
const TEAL_LIGHT= '#f0fdfa'
const TEAL_MID  = '#99f6e4'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const ROLES = [
  { value: 'admin',      label: 'Admin',      color: TEAL_DARK, bg: TEAL_LIGHT, border: TEAL_MID,    desc: 'Full clinic access' },
  { value: 'therapist',  label: 'Therapist',  color: '#1d4ed8', bg: '#eff6ff',  border: '#bfdbfe',   desc: 'Patient care only' },
  { value: 'front_desk', label: 'Front Desk', color: '#7c3aed', bg: '#f5f3ff',  border: '#ddd6fe',   desc: 'Scheduling & intake' },
]

const roleInfo = Object.fromEntries(ROLES.map(r => [r.value, r]))

export default function StaffManagement({ clinicId, currentUserId, onToast }) {
  const [staff, setStaff]                   = useState([])
  const [therapistCount, setTherapistCount] = useState(0)
  const [therapistLimit, setTherapistLimit] = useState(null)
  const [plan, setPlan]                     = useState(null)
  const [loading, setLoading]               = useState(true)
  const [showAdd, setShowAdd]               = useState(false)
  const [addForm, setAddForm]               = useState({ username: '', password: '', confirmPassword: '', role: 'therapist' })
  const [addError, setAddError]             = useState('')
  const [addLoading, setAddLoading]         = useState(false)
  const [confirmDelete, setConfirmDelete]   = useState(null)

  const load = () => {
    if (!clinicId) { setLoading(false); return }
    setLoading(true)
    axios.get(`${API}/auth/clinic/${clinicId}/staff`)
      .then(res => {
        setStaff(res.data.staff)
        setTherapistCount(res.data.therapist_count)
        setTherapistLimit(res.data.therapist_limit)
        setPlan(res.data.plan)
      })
      .catch(() => onToast?.('Failed to load staff', 'error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [clinicId])

  const handleAdd = () => {
    setAddError('')
    if (!addForm.username.trim() || !addForm.password) { setAddError('Please fill in all fields.'); return }
    if (addForm.password !== addForm.confirmPassword) { setAddError('Passwords do not match.'); return }
    if (addForm.password.length < 6) { setAddError('Password must be at least 6 characters.'); return }
    setAddLoading(true)
    axios.post(`${API}/auth/create-user`, {
      username: addForm.username.trim(), password: addForm.password,
      role: addForm.role, clinic_id: clinicId, patient_id: null,
    })
      .then(() => {
        onToast?.(`${addForm.username} added as ${roleInfo[addForm.role]?.label || addForm.role}`, 'success')
        setAddForm({ username: '', password: '', confirmPassword: '', role: 'therapist' })
        setShowAdd(false)
        load()
      })
      .catch(err => setAddError(err.response?.data?.detail || 'Failed to add staff member.'))
      .finally(() => setAddLoading(false))
  }

  const handleRoleChange = (userId, newRole) => {
    axios.patch(`${API}/auth/users/${userId}/role`, { role: newRole })
      .then(() => { onToast?.('Role updated', 'success'); load() })
      .catch(err => onToast?.(err.response?.data?.detail || 'Failed to update role', 'error'))
  }

  const handleDelete = (userId) => {
    axios.delete(`${API}/auth/users/${userId}`)
      .then(() => { onToast?.('Staff member removed', 'success'); setConfirmDelete(null); load() })
      .catch(err => onToast?.(err.response?.data?.detail || 'Failed to remove user', 'error'))
  }

  const atLimit = therapistLimit && therapistCount >= therapistLimit

  return (
    <div style={{ fontFamily: FONT }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px' }}>Staff Members</h2>
          <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>Manage who has access to your ClearPath PT account.</p>
        </div>
        <button
          onClick={() => { setShowAdd(s => !s); setAddError('') }}
          style={{ padding: '10px 20px', background: showAdd ? '#f1f5f9' : TEAL, color: showAdd ? '#64748b' : 'white', border: showAdd ? '1.5px solid #e2e8f0' : 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: FONT }}
        >
          {showAdd ? ' Cancel' : '+ Add Staff Member'}
        </button>
      </div>

      {/* Plan / limit banner */}
      {therapistLimit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: atLimit ? '#fef2f2' : TEAL_LIGHT, border: `1.5px solid ${atLimit ? '#fecaca' : TEAL_MID}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '24px' }}>
          <div style={{ flex: 1, fontSize: '14px', color: atLimit ? '#dc2626' : TEAL_DARK, fontWeight: '500' }}>
            {atLimit
              ? ` Therapist limit reached (${therapistLimit}) on ${plan} plan.`
              : `${therapistCount} of ${therapistLimit} therapist seat${therapistLimit !== 1 ? 's' : ''} used · ${plan} plan`}
          </div>
          {atLimit && <a href="/settings" style={{ fontSize: '13px', fontWeight: '600', color: '#dc2626', textDecoration: 'underline' }}>Upgrade</a>}
        </div>
      )}

      {/* Add Staff form */}
      {showAdd && (
        <div style={{ background: 'rgba(248,250,252,0.78)', border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>New Staff Member</div>

          {/* Role selector first */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Role</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {ROLES.map(r => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setAddForm(f => ({ ...f, role: r.value }))}
                  style={{
                    padding: '10px 8px', borderRadius: '8px', textAlign: 'center',
                    border: addForm.role === r.value ? `2px solid ${r.color}` : '2px solid #e2e8f0',
                    background: addForm.role === r.value ? r.bg : 'white',
                    color: addForm.role === r.value ? r.color : '#94a3b8',
                    cursor: 'pointer', fontFamily: FONT, transition: 'all 0.12s',
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: '700' }}>{r.label}</div>
                  <div style={{ fontSize: '11px', fontWeight: '400', marginTop: '2px' }}>{r.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
            <FormField label="Username">
              <input placeholder="e.g. dr.smith" value={addForm.username} onChange={e => setAddForm(f => ({ ...f, username: e.target.value }))} style={iS} autoCapitalize="none" />
            </FormField>
            <FormField label="Password">
              <input type="password" placeholder="Min 6 characters" value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} style={iS} />
            </FormField>
            <FormField label="Confirm Password">
              <input type="password" placeholder="Re-enter" value={addForm.confirmPassword} onChange={e => setAddForm(f => ({ ...f, confirmPassword: e.target.value }))} style={iS} />
            </FormField>
          </div>

          {addError && <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontSize: '14px', marginBottom: '14px' }}>{addError}</div>}

          <button onClick={handleAdd} disabled={addLoading} style={{ padding: '10px 24px', background: TEAL, color: 'white', border: 'none', borderRadius: '8px', cursor: addLoading ? 'default' : 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: FONT, opacity: addLoading ? 0.7 : 1 }}>
            {addLoading ? 'Adding...' : 'Add Staff Member'}
          </button>
        </div>
      )}

      {/* Staff list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading staff...</div>
      ) : staff.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', background: '#f8fafc', borderRadius: '12px', border: '1.5px dashed #e2e8f0', color: '#94a3b8' }}>No staff members found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {staff.map(member => {
            const ri    = roleInfo[member.role] || roleInfo.therapist
            const isSelf = member.id === currentUserId
            return (
              <div key={member.id} style={{ background: 'rgba(255,255,255,0.78)', border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '14px 18px' }}>

                {/* Top row: avatar + name + (you) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: isSelf ? 0 : '12px' }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: ri.bg, border: `2px solid ${ri.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '700', color: ri.color, flexShrink: 0 }}>
                    {member.username[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a' }}>{member.username}</span>
                      {isSelf && <span style={{ fontSize: '11px', color: '#94a3b8' }}>(you)</span>}
                      <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '99px', background: ri.bg, color: ri.color, border: `1px solid ${ri.border}` }}>
                        {ri.label}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{ri.desc}</div>
                  </div>

                  {/* Remove — only for other users */}
                  {!isSelf && (
                    confirmDelete === member.id ? (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', color: '#dc2626', fontWeight: '500' }}>Remove?</span>
                        <button onClick={() => handleDelete(member.id)} style={{ padding: '5px 10px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: FONT }}>Yes</button>
                        <button onClick={() => setConfirmDelete(null)} style={{ padding: '5px 10px', background: 'white', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: FONT }}>No</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDelete(member.id)}
                        style={{ padding: '5px 12px', background: 'white', color: '#94a3b8', border: '1.5px solid #e2e8f0', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontFamily: FONT }}
                        onMouseOver={e => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca' }}
                        onMouseOut={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = '#e2e8f0' }}
                      >
                        Remove
                      </button>
                    )
                  )}
                </div>

                {/* Role toggle buttons — only for other users */}
                {!isSelf && (
                  <div style={{ display: 'flex', gap: '6px', paddingLeft: '50px' }}>
                    {ROLES.map(r => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => r.value !== member.role && handleRoleChange(member.id, r.value)}
                        style={{
                          padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                          border: `1.5px solid ${member.role === r.value ? r.color : '#e2e8f0'}`,
                          background: member.role === r.value ? r.bg : 'white',
                          color: member.role === r.value ? r.color : '#cbd5e0',
                          cursor: member.role === r.value ? 'default' : 'pointer',
                          fontFamily: FONT, transition: 'all 0.12s',
                        }}
                      >
                        {r.label}
                      </button>
                    ))}
                    <span style={{ fontSize: '11px', color: '#94a3b8', alignSelf: 'center', marginLeft: '4px' }}>Click to change role</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Role permissions reference */}
      <div style={{ marginTop: '28px', background: 'rgba(248,250,252,0.78)', borderRadius: '10px', padding: '16px 18px', border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Role Permissions</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {[
            { role: 'admin',      items: ['Manage all patients', 'Add / remove staff', 'Clinic settings & billing', 'Full reports & audit logs'] },
            { role: 'therapist',  items: ['View & manage patients', 'Log sessions & SOAP notes', 'Treatment plans & goals', 'Manage appointments'] },
            { role: 'front_desk', items: ['Manage appointments', 'View basic patient info', 'Send & track intake forms', 'Patient check-in'] },
          ].map(({ role, items }) => {
            const r = roleInfo[role]
            return (
              <div key={role}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: r.color, marginBottom: '6px' }}>{r.label}</div>
                <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', color: '#64748b', lineHeight: '1.8' }}>
                  {items.map(item => <li key={item}>{item}</li>)}
                </ul>
              </div>
            )
          })}
        </div>
      </div>

    </div>
  )
}

function FormField({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '5px' }}>{label}</label>
      {children}
    </div>
  )
}

const iS = {
  width: '100%', padding: '9px 11px', fontSize: '14px',
  border: '1.5px solid #e2e8f0', borderRadius: '7px',
  boxSizing: 'border-box', color: '#1e293b', background: 'white',
  fontFamily: FONT, outline: 'none',
}
